import type { DeploymentSchedule } from '../types/account';

const normalize = (value: unknown): string =>
  String(value || '')
    .trim()
    .toLowerCase();
const positiveNumber = (value: unknown): boolean => Number(value || 0) > 0;

export const DEPLOYMENT_ACTIVE_STATUSES = new Set([
  'running',
  'running_once',
  'scheduled',
  'in_progress',
  'initiated',
  'queued',
  'building',
  'dependency_building',
  'deploying',
  'pulling',
  'starting',
  'waiting_for_capacity',
]);

const DEPLOYMENT_STATUS_LABELS: Record<string, string> = {
  running: 'Running',
  running_once: 'Running',
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  pulling: 'Pulling',
  starting: 'Starting',
  waiting_for_capacity: 'Waiting for Capacity',
  completed: 'Completed',
  completed_with_errors: 'Completed with Errors',
  failed: 'Failed',
  stopped: 'Stopped',
  force_stopped: 'Stopped',
  terminated: 'Stopped',
  terminated_timeout: 'Stopped',
  queued: 'Queued',
  building: 'Building',
  dependency_building: 'Building',
  deploying: 'Deploying',
  initiated: 'Initiated',
  not_found: 'Not Found',
  stop_failed: 'Stop Failed',
};

const DEGRADED_REASON_LABELS: Record<string, string> = {
  artifact_errors: 'Degraded: artifact errors',
  last_run_overdue: 'Degraded: schedule overdue',
  schedule_backend_missing: 'Degraded: schedule backend missing',
  schedule_backend_disabled: 'Degraded: schedule backend disabled',
  schedule_backend_target_missing: 'Degraded: schedule target missing',
  schedule_backend_error: 'Degraded: schedule backend error',
  schedule_launch_failed: 'Degraded: schedule launch failed',
  schedule_launch_error: 'Degraded: schedule launch error',
  schedule_disabled: 'Degraded: schedule disabled',
  schedule_not_found: 'Degraded: schedule missing',
  schedule_disable_failed: 'Degraded: stop failed',
  schedule_stop_failed: 'Degraded: stop failed',
  last_run_failed: 'Degraded: last run failed',
};

export const formatDeploymentLifecycleStatusLabel = (status?: unknown): string => {
  const normalized = normalize(status);
  if (!normalized) return 'Unknown';
  return (
    DEPLOYMENT_STATUS_LABELS[normalized] ||
    normalized
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
  );
};

export const isDeploymentScheduleStopped = (schedule?: DeploymentSchedule | null): boolean => {
  const scheduleStatus = normalize(schedule?.scheduleStatus);
  const stopStatus = normalize(schedule?.scheduleStopStatus);
  return (
    normalize(schedule?.health) === 'stopped' ||
    scheduleStatus === 'deleted' ||
    stopStatus === 'deleted' ||
    stopStatus === 'not_found' ||
    Boolean(schedule?.scheduleDeletedAt)
  );
};

export const hasDeploymentScheduleMetadata = (
  status?: unknown,
  schedule?: DeploymentSchedule | null,
): boolean => {
  const normalizedStatus = normalize(status);
  const deploymentMode = normalize(schedule?.deploymentMode);
  return normalizedStatus === 'scheduled' || deploymentMode === 'scheduled' || Boolean(schedule);
};

export const getDeploymentScheduleDisplayHealth = (
  status?: unknown,
  schedule?: DeploymentSchedule | null,
): string => {
  if (isDeploymentScheduleStopped(schedule)) return 'stopped';
  return normalize(schedule?.health);
};

const formatRunStatus = (status: unknown): string => String(status || '').replace(/_/g, ' ');

export const formatDeploymentScheduleDetail = (
  status?: unknown,
  schedule?: DeploymentSchedule | null,
): string | null => {
  if (!hasDeploymentScheduleMetadata(status, schedule)) return null;
  const health = getDeploymentScheduleDisplayHealth(status, schedule);
  const lastRunStatus = normalize(schedule?.lastRunStatus);

  if (isDeploymentScheduleStopped(schedule)) return 'Schedule stopped';

  if (health === 'degraded') {
    const healthReason = normalize(schedule?.healthReason);
    if (DEGRADED_REASON_LABELS[healthReason]) return DEGRADED_REASON_LABELS[healthReason];
    if (lastRunStatus.includes('artifact_error')) return 'Degraded: artifact errors';
    if (positiveNumber(schedule?.lastRunOverdueBySeconds)) return 'Degraded: schedule overdue';
    if (lastRunStatus.includes('failed') || lastRunStatus.includes('error'))
      return 'Degraded: last run failed';
    return `Degraded${lastRunStatus ? `: ${formatRunStatus(lastRunStatus)}` : ''}`;
  }

  if (
    health === 'healthy' &&
    (lastRunStatus === 'succeeded' ||
      lastRunStatus === 'completed' ||
      lastRunStatus.startsWith('succeeded_'))
  ) {
    return 'Last run OK';
  }
  if (lastRunStatus) return `Last run: ${formatRunStatus(lastRunStatus)}`;
  return null;
};

/** Customer summaries describe activity and problems; scheduler diagnostics stay in bot details. */
export const formatDeploymentSummaryStatus = (
  status?: string | null,
  schedule?: DeploymentSchedule | null,
): string => {
  if (isDeploymentScheduleStopped(schedule)) return 'Stopped';
  if (normalize(schedule?.health) === 'degraded') return 'Needs attention';
  const normalized = normalize(status);
  if (normalized === 'scheduled') return 'Active';
  if (
    [
      'initiated',
      'queued',
      'building',
      'dependency_building',
      'deploying',
      'pulling',
      'waiting_for_capacity',
      'in_progress',
    ].includes(normalized)
  )
    return 'Starting';
  return formatDeploymentLifecycleStatusLabel(status);
};

export const formatDeploymentSummaryWarning = (
  schedule?: DeploymentSchedule | null,
): string | null => {
  if (isDeploymentScheduleStopped(schedule) || normalize(schedule?.health) !== 'degraded')
    return null;
  const reason = normalize(schedule?.healthReason);
  if (reason === 'artifact_errors' || normalize(schedule?.lastRunStatus).includes('artifact_error'))
    return 'Results unavailable';
  if (reason === 'last_run_overdue' || positiveNumber(schedule?.lastRunOverdueBySeconds))
    return 'Run delayed';
  if (['schedule_disable_failed', 'schedule_stop_failed'].includes(reason)) return 'Could not stop';
  if (reason === 'last_run_failed') return 'Last run failed';
  if (['schedule_launch_failed', 'schedule_launch_error'].includes(reason))
    return 'Could not start';
  return 'Open bot to review';
};
